/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import React from 'react';
import { Node } from '@msdyn365-commerce-modules/utilities';
// @ts-ignore
import SelectableCard from '../../themes/mattressfirm/views/components/selectable-card';
import TitledModal, { TitledModalButton } from '../../themes/mattressfirm/views/components/titled-modal';

import { IMfrmChooseYourFoundationViewProps } from './mfrm-choose-your-foundation';
import { renderRating } from '../../Utilities/get-rating';
import { getFallbackImageUrl } from '../../data-actions/utilities/utils';
import { Tabs } from '../../themes/mattressfirm/custom-components/tabs';
import { SelectableCards } from '../../themes/mattressfirm/custom-components/selectable-cards';
import { renderBaseImage } from './util/renderBaseImage';
import { hasDiscount } from './util/hasDiscount';
import { getDisplayedBaseName } from './util/getDisplayedBaseName';
import { getTabs } from './util/getTabs';
import { getKeys } from './util/getKeys';
import { Badge } from '../../themes/mattressfirm/custom-components/badge';
import CustomSkeleton from '../../themes/mattressfirm/custom-components/CustomSkeleton';

export default (props: IMfrmChooseYourFoundationViewProps) => {
    const { product, splitBases, selectedBaseId, selectedTab, onClickCard, onClickTab, isLoading } = props;
    let { bases } = props;
    bases = splitBases.concat(bases);
    const { addABaseTitle, addABaseSubtitle } = props.config;
    const { data: { categoryPaths: { result } } } = props;
    let parentCategory = result && result[0]?.Name ? result[0]?.Name?.toLowerCase() : '';
    parentCategory = parentCategory === 'mattresses' || parentCategory === 'pillows' || parentCategory === 'bedding' ? parentCategory : 'default';
    const [isAdjustableModalVisible, setIsAdjustableModalVisible] = React.useState<boolean>(false);
    const selectedBase = bases.find(base => base.RecordId === selectedBaseId)!;
    const renderBadge = (key: number) => {
        const base = bases.find(base => base.RecordId === key)!;
        if (base?.outOfStock) {
            return <Badge color="charcoal">Out of Stock</Badge>;
        }
        return null;
    };
    const renderCard = (key: number | 'Mattress Only') => {
        if (key === 'Mattress Only') {
            return (
                <SelectableCard
                    productName={props.config.noSelectionText}
                    img={renderBaseImage(
                        props.context.request.gridSettings,
                        product?.PrimaryImageUrl,
                        product?.Name,
                        getFallbackImageUrl(product?.ItemId, props.context.request.apiSettings)
                    )}
                />
            );
        }
        const base = bases.find(base => base.RecordId === key)!;
        return (
            <SelectableCard
                img={renderBaseImage(
                    props.context.request.gridSettings,
                    base.PrimaryImageUrl,
                    base.Name,
                    getFallbackImageUrl(base.ItemId, props.context.request.apiSettings)
                )}
                eyebrowText={base.isRecommended ? 'Recommended' : ''}
                productName={getDisplayedBaseName(base)}
                ratings={renderRating(
                    props.context,
                    props.typeName,
                    props.id,
                    base.AverageRating,
                    base.TotalRatings,
                    props.resources.ratingAriaLabel
                )}
                standardPrice={
                    typeof base.CustomerContextualPrice === 'number' &&
                    typeof base.DiscountAmount === 'number' &&
                    base.CustomerContextualPrice + base.DiscountAmount
                }
                currentPrice={hasDiscount(base) && base.CustomerContextualPrice}
                isActive={!base.outOfStock}
            />
        );
    };

    const handleClickButton = () => {
        setIsAdjustableModalVisible((isAdjustableModalVisible: boolean) => !isAdjustableModalVisible);
    };

    if (isLoading) {
        return (<CustomSkeleton type='text' className={`foundation-skeleton-${parentCategory}`} />);
    }
    if (bases.length < 1) {
        return null;
    }

    return (
        <div className='choose-your-foundation-button'>
            <Node className='ms-buybox__configure'>
                <TitledModalButton isAdjustableModalVisible={isAdjustableModalVisible} onClick={handleClickButton}>
                    {selectedBaseId !== 'Mattress Only' ? (
                        <span>
                            Your foundation: {getDisplayedBaseName(selectedBase)} +{' '}
                            <span className={hasDiscount(selectedBase) ? 'discount' : ''}>${selectedBase.CustomerContextualPrice}</span>
                        </span>
                    ) : (
                        props.config.label
                    )}
                </TitledModalButton>
            </Node>
            <TitledModal
                isOpen={isAdjustableModalVisible}
                isMobile={false}
                distinctClassName={'choose-your-foundation-modal'}
                onCloseModal={() => {
                    setIsAdjustableModalVisible(!isAdjustableModalVisible);
                }}
                title={addABaseTitle}
                subtitle={addABaseSubtitle}
            >
                <Tabs
                    tabs={getTabs(bases)}
                    selectedTab={selectedTab}
                    onClickTab={(tab: 'All' | 'Adjustable Bases' | 'Box Springs') => onClickTab(tab)}
                />
                <div className='adjustable-base-modal-content'>
                    <SelectableCards
                        selected={selectedBaseId}
                        keys={getKeys(bases, selectedTab)}
                        onClickCard={(key: number | 'Mattress Only', index: number) => {
                            onClickCard(key, index);
                            setIsAdjustableModalVisible(false);
                        }}
                        renderCard={renderCard}
                        renderBadge={renderBadge}
                        disabled={(key: number | 'Mattress Only') => {
                            return bases.find(base => base.RecordId === key)?.outOfStock;
                        }}
                    />
                </div>
            </TitledModal>
        </div>
    );
};
